import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { AxisResetSelectionComponent } from './resetSelection.component';
import { ButtonSharedService } from './button.sharedService';

@Component({
  selector: '[axis-button]',
  template: '<ng-content></ng-content>',
  host: {
    '[attr.title]': 'title',
    '[attr.type]': 'type',
    class: 'axis-btn'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ButtonComponent),
      multi: true
    }
  ]
})
export class ButtonComponent {
  @Input() title = null;
  @Input() type = 'button';
  private propagateModelChange = (_: any) => { };
  private onTouched: any = (_: any) => { };
  writeValue(value: any) {
  }
  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  validate(control: AbstractControl): { [key: string]: any } {
    return null;
  }
}

@Component({
  selector: 'axis-button-group',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-btn-group',
    role: 'group'
  }
})
export class ButtonGroupComponent { }

@Component({
  selector: 'axis-button-group-vertical',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-btn-group-vertical',
    role: 'group'
  }
})
export class ButtonGroupVerticalComponent { }

@Component({
  selector: 'axis-button-toolbar',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-btn-toolbar',
    role: 'toolbar'
  }
})
export class ButtonToolbarComponent { }

@Component({
  selector: 'axis-button-toggle',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-btn-toggle axis-btn-group',
    role:'group'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonToggleComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ButtonToggleComponent),
      multi: true
    }
  ]
})
export class ButtonToggleComponent implements AfterContentInit, OnDestroy {
  @Input() disabled = false;
  @Input() isReset = false;
  @Input() rel: string;
  @Input() required = false;
  @Input() font = 'material';

  @Output() getSelectedIndex = new EventEmitter();
  @Output() getSelectedValue = new EventEmitter();
  @Output() resetEvent = new EventEmitter();

  private buttonCompRef: any;
  private isResetShown = false;
  private isOnLoad = true;

  public ButtonToggleChildArray = new Array();
  public selectedIndex: number;
  public selectedValue: string | number | boolean;

  private model: any;
  private isIndex = false;
  private propagateModelChange = (_: any) => { };
  private OnTouchedEvent = () => { };

  constructor(
    private viewContainerRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private buttonSharedService: ButtonSharedService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngAfterContentInit() {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent, index: number) => {
        component.changeDetect.subscribe(() => {
          this.toggleOther(component);
        });
        component.emitBlurEvent.subscribe(() => {
          this.OnTouchedEvent();
        });
      }
    );
    if (this.model === false || this.model || parseInt(this.model, 10) === 0) {
      this.writeValue(this.model);
    }
  }
  // Implementing ControlValueAccessor for RadioButtonWrapperComponent
  // ::For custom form validations(triggered when assigned to ngModel)
  writeValue(value: any) {
    if ((value === false || value || value === 0) && value !== -1) {
      this.model = value;
      this.ButtonToggleChildArray.forEach(
        (component: ButtonToggleChildComponent, index: number) => {
          if (component.value || component.value === false) {
            this.isIndex = false;
            if (this.model.toString() === component.value.toString()) {
              component.active = true;
              this.selectedIndex = index;
              this.selectedValue = component.value;
            } else {
              component.active = false;
            }
          } else {
            this.isIndex = true;
            if (this.model === index) {
              component.active = true;
              this.selectedIndex = index;
              this.selectedValue = component.value;
            } else {
              component.active = false;
            }
          }
          if (!component._changeDetRef['destroyed']) {
            component._changeDetRef.detectChanges();
          }
        }
      );
    } else {
      this.reset();
      this.isOnLoad = false;
    }
  }
  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.OnTouchedEvent = fn;
  }
  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      if (control.value !== -1) {
        return null;
      }

      return { axisTogglebutton: { valid: false } };
    }
    return null;
  }

  setDisabledState(isDisabled: boolean) {
    this.ButtonToggleChildArray.forEach((comp: ButtonToggleChildComponent) => {
      comp.buttonToggle.disabled = isDisabled;
    });
  }

  enableResetIcon() {
    const key = this.buttonSharedService.generateUniqueKey();
    this.buttonSharedService.setInstance(this, key);
    const resetSelectionCompFactory = this.resolver.resolveComponentFactory(
      AxisResetSelectionComponent
    );
    this.buttonCompRef = this.viewContainerRef.createComponent(
      resetSelectionCompFactory
    );
    this.buttonCompRef.instance.uniqueKey = key;
    this.buttonCompRef.instance.markForCheck();
  }

  disableResetIcon() {
    if (this.buttonCompRef) {
      this.buttonCompRef.destroy();
      this.buttonCompRef = null;
    }
  }

  predefinedselection(comp) {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent, index: number) => {
        if (comp === component) {
          component.isActive = true;
          this.selectedIndex = index;
          this.selectedValue = comp.value;
          if (!this.isOnLoad) {
            this.getSelectedIndex.emit(this.selectedIndex);
            this.getSelectedValue.emit(this.selectedValue);
          }
          if (this.isReset && !this.isResetShown) {
            this.enableResetIcon();
            this.isResetShown = true;
          }
        } else {
          component.isActive = false;
        }
      }
    );
    this.isOnLoad = false;
  }
  toggleOther(comp) {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent, index: number) => {
        if (comp === component) {
          component.isActive = true;
          this.selectedIndex = index;
          this.selectedValue = comp.value;
          if ((comp.value || comp.value === false) && !this.isIndex) {
            this.propagateModelChange(this.selectedValue);
          } else {
            this.propagateModelChange(this.selectedIndex);
          }
          this.getSelectedIndex.emit(this.selectedIndex);
          this.getSelectedValue.emit(this.selectedValue);
          if (this.isReset && !this.isResetShown) {
            this.enableResetIcon();
            this.isResetShown = true;
          }
        } else {
          component.isActive = false;
        }
      }
    );
  }

  clearToggle() {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent) => {
        component.isActive = false;
      }
    );
  }

  resetToggle() {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent) => {
        component.isActive = false; // reset all, then:
        if (component.active) {
          // data-active setting
          component.isActive = true;
        }
      }
    );
  }

  reset() {
    this.resetSelection();
  }
  resetAll() {
    this.resetSelection();
    this.propagateModelChange(this.selectedIndex);
    this.propagateModelChange(this.selectedValue);
  }
  resetSelection() {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent) => {
        component.isActive = false;
      }
    );
    this.disableResetIcon();
    this.isResetShown = false;
    this.selectedIndex = null;
    this.selectedValue = null;
    this.isIndex = false;
    if (!this.isOnLoad) {
      this.getSelectedIndex.emit(this.selectedIndex);
      this.getSelectedValue.emit(this.selectedValue);
      this.resetEvent.emit();
    }
  }
  disableToggleBtn() {
    this.disabled = true;
  }
  enableToggleBtn() {
    this.disabled = false;
  }
  disableEnableToggleBtn() {
    this.disabled = !this.disabled;
  }
  ngOnDestroy() {
    this.ButtonToggleChildArray.forEach(
      (component: ButtonToggleChildComponent, index: number) => {
        component.changeDetect.unsubscribe();
        component.emitBlurEvent.unsubscribe();
      }
    );
    this.ButtonToggleChildArray = new Array();
  }
}

@Component({
  selector: 'axis-button-toggle-label',
  template: `<button axis-button [class.active]='isActive' [attr.aria-label]='ariaLabel' [attr.aria-pressed]='isActive'
      (click)='!buttonToggle.disabled && toggleSwitch()' (blur)="emitOnBlurEvent()"
      [attr.disabled]="(buttonToggle.disabled || disabled)?'disabled':null">
          <ng-content></ng-content>
      </button>`,
  host: { class: 'axis-btn-toggle-label' }
})
export class ButtonToggleChildComponent {
  private _active: boolean;
  public isActive = false;
 @Input() public ariaLabel: string;

  @Input()
  public set active(value: boolean) {
    this._active = value;
    if (this._active) {
      this.buttonToggle.predefinedselection(this);
    }
  }
  @Input() disabled = false;
  @Input() value: string | number | boolean;

  @Output() changeDetect = new EventEmitter();
  @Output() emitBlurEvent = new EventEmitter();

  constructor(public buttonToggle: ButtonToggleComponent,
    public _changeDetRef: ChangeDetectorRef) {
    this.buttonToggle.ButtonToggleChildArray.push(this);
  }

  toggleSwitch() {
    this.changeDetect.emit(this._active);
  }

  emitOnBlurEvent() {
    this.emitBlurEvent.emit();
  }
}

@Component({
  selector: 'axis-toggle-switch',
  template: `
  <ng-container *ngIf="!buttonToggleSwitch">
    <input type="checkbox"
      [attr.checked]="checked ? 'checked' : null"
      [attr.aria-label]='ariaLabel'
      [attr.disabled]="disabled ? 'disabled' : null"
      [attr.id]="id"
      class="axis-toggle-switch-checkbox"
      tabindex="-1">
    <label [attr.for]="id" class="axis-toggle-switch-label">
      <ng-container *ngTemplateOutlet="tempOutlet"></ng-container>
    </label>
  </ng-container>
  <ng-container *ngIf="buttonToggleSwitch">
    <ng-container *ngTemplateOutlet="tempOutlet"></ng-container>
  </ng-container>
  <ng-template #tempOutlet>
    <ng-content></ng-content>
  </ng-template>
  `,
  host: {
    '[attr.aria-checked]':'toggleFlag',
    '[attr.disabled]': 'isDisabled',
    '[class.active]': 'toggleFlag',
    class: 'axis-toggle-switch',
    role: 'switch',
    tabindex: '0',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleSwitchComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ToggleSwitchComponent),
      multi: true
    }
  ]
})
export class ToggleSwitchComponent implements OnInit, OnChanges, AfterContentInit, OnDestroy {

  @Input() public ariaLabel: string;
  @Input() disabled;
  @Input() checked;
  @Input('cid') id: string;
  @Input() rel: string;
  @Input() required = false;

  @Output() onToggle = new EventEmitter();

  public ariaLabelText = '';
  public buttonToggleSwitch: any;
  public isDisabled: string;
  public toggleFlag = false;

  private propagateModelChange = (_: any) => { };
  private OnTouchedEvent = () => { };

  ngOnInit(): void {
    this.getChecked();
    if (!this.id) {
      this.id = (Math.random() + Math.random()).toString().replace('.', '');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['checked']) {
      if (changes['checked']['currentValue'] === 'checked' || changes['checked']['currentValue'] === true) {
        this.toggleFlag = true;
      } else {
        this.toggleFlag = false;
      }
    }
  }

  ngAfterContentInit() {
    // based on buttonToggleSwitch we are differentiating old and new toggle switch.
    if (this.buttonToggleSwitch) {
      this.buttonToggleSwitch.changeDetect.subscribe(() => {
        this.OnTouchedEvent();
      });
      this.buttonToggleSwitch.emitBlurEvent.subscribe(() => {
        this.OnTouchedEvent();
      });
    }
  }

  // check and set the aria-label message
  getChecked(): void {
    let message = 'not checked';
    if (this.toggleFlag) {
      message = 'checked';
    }
    this.ariaLabelText = this.ariaLabel + ' ' + message; // overridden by aria-labelledby
  }

  writeValue(value: any) {
    if (value && value !== -1) {
      this.toggleFlag = true;
      this.checked = true;
    } else {
      this.toggleFlag = false;
      this.checked = false;
    }
    this.getChecked();
  }

  @HostListener('click', ['$event'])
  @HostListener('keydown', ['$event'])
  toggleActive(event: any): void {
    if (!this.disabled) {
      if (event.key) {
        const key = event.key.toLocaleLowerCase();
        if (key === ' ' || key === 'enter') {
          event.preventDefault();
        } else {
          return;
        }
      }
      event.stopPropagation();
      event.preventDefault();
      this.toggleFlag = !this.toggleFlag;
      this.checked = this.toggleFlag;
      this.propagateModelChange(this.toggleFlag);
      this.onToggle.emit(this.toggleFlag);
      if (this.buttonToggleSwitch) {
        this.buttonToggleSwitch.changeDetect.emit(this.toggleFlag);
      }
      this.getChecked();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.OnTouchedEvent = fn;
  }

  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      if (control.value !== -1) {
        return null;
      }

      return { axisToggleswitch: { valid: false } };
    }
    return null;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isDisabled = 'disabled';
    } else {
      this.isDisabled = '';
    }
  }

  ngOnDestroy() {
    if (this.buttonToggleSwitch) {
      this.buttonToggleSwitch.changeDetect.unsubscribe();
      this.buttonToggleSwitch.emitBlurEvent.unsubscribe();
    }
  }
}

@Component({
  selector: 'axis-toggle-bar',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-toggle-bar'
  }
})
export class ToggleBarComponent { }

@Component({
  selector: 'axis-toggle-button',
  template: `<div (blur)="emitOnBlurEvent()"><ng-content></ng-content></div>`,
  host: {
    class: 'axis-toggle-button'
  }
})
export class ToggleButtonComponent {
  @Output() changeDetect = new EventEmitter();
  @Output() emitBlurEvent = new EventEmitter();

  constructor(public toggleSwitchComponent: ToggleSwitchComponent) {
    this.toggleSwitchComponent.buttonToggleSwitch = this;
  }

  emitOnBlurEvent() {
    this.emitBlurEvent.emit();
  }
}

@Component({
  selector: 'axis-toggle-container',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-toggle-container'
  }
})
export class ToggleContainerComponent { }

@Component({
  selector: 'axis-toggle-label',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-toggle-label'
  }
})
export class ToggleLabelComponent { }

@Component({
  selector: 'axis-button-wrapper',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-btn-wrapper'
  }
})
export class ButtonWrapperComponent { }
